import React, {useState} from 'react';
import Page from "../../../components/Page";
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import 'mapbox-gl/dist/mapbox-gl.css';
import "../../../services/mabox-gl";
import VisitorsMovementMap from "../../../components/Maps/VisitorsMovementMap";
import Card from "../../../components/Card";
import DateInput from "../../../components/InsightControls/DateInput";
import SiteSelect from "../../../components/InsightControls/SiteSelect";
import {useOperator} from "../../../context/OperatorContext";
import {DateTime} from "luxon";
import {Site} from "../../../types/Site";
import Selector from "../../../components/Form/Selector";
import {Item} from "../../../components/Form/Selector/Selector";

// Helpers ----

const breadcrumbs = {
  "Insights": '/',
  "Visitors": '/insights/visitors',
  'Movement': '/insights/visitors/movement',
}

// Follow visitor
export const FOLLOW_VISITOR = {
  'YES': 'yes',
  'NO': 'no',
} as const

const followVisitorItems: Item<FollowUser>[] = [
  {id: FOLLOW_VISITOR.NO, name: "No"},
  {id: FOLLOW_VISITOR.YES, name: "Yes"},
]

// Distance threshold
export const DISTANCE_THRESHOLD = {
  '10': 10,
  '25': 25,
  '50': 50,
  '100': 100,
  '250': 250,
  '500': 500,
  '1000': 1000,
} as const

const distanceThresholdItems: Item<DistanceThreshold>[] = [
  {id: DISTANCE_THRESHOLD["10"], name: "10 metres"},
  {id: DISTANCE_THRESHOLD["25"], name: "25 metres"},
  {id: DISTANCE_THRESHOLD["50"], name: "50 metres"},
  {id: DISTANCE_THRESHOLD["100"], name: "100 metres"},
  {id: DISTANCE_THRESHOLD["250"], name: "250 metres"},
  {id: DISTANCE_THRESHOLD["500"], name: "500 metres"},
  {id: DISTANCE_THRESHOLD["1000"], name: "1 kilometre"},
]

// Zoom levels
export const ZOOM_LEVELS = {
  '1x': 15,
  '2x': 17,
  '3x': 19,
} as const

const zoomLevelItems: Item<ZoomLevel>[] = [
  {id: ZOOM_LEVELS["1x"], name: "1x"},
  {id: ZOOM_LEVELS["2x"], name: "2x"},
  {id: ZOOM_LEVELS["3x"], name: "3x"},
]

// Default filters
const defaultFilters: Filters = {
  since: DateTime.now().minus({days: 14}).startOf('day'),
  until: DateTime.now().endOf('day'),
  siteId: null,
  followVisitor: FOLLOW_VISITOR.YES,
  distanceThreshold: DISTANCE_THRESHOLD["10"],
  zoomLevel: ZOOM_LEVELS["2x"],
}

// Types ----

export type FollowUser = typeof FOLLOW_VISITOR[keyof typeof FOLLOW_VISITOR];
export type DistanceThreshold = typeof DISTANCE_THRESHOLD[keyof typeof DISTANCE_THRESHOLD];
export type ZoomLevel = typeof ZOOM_LEVELS[keyof typeof ZOOM_LEVELS];

type Filters = {
  since: DateTime,
  until: DateTime,
  siteId: number | null,
  followVisitor: FollowUser,
  distanceThreshold: DistanceThreshold,
  zoomLevel: ZoomLevel,
}

// Page ----

export function VisitorMovement() {
  const {termFor, isMultiSite} = useOperator();

  const [filters, setFilters] = useState<Filters>({...defaultFilters});

  // Helpers
  function selectDate(since: DateTime) {
    setFilters(currentFilters => {
      return {
        ...currentFilters,
        since: since.startOf('day'),
        until: since.endOf('day'),
      };
    })
  }

  function selectSite(site: Site | null) {
    setFilters(currentFilters => {
      return {...currentFilters, siteId: site?.id ?? null};
    })
  }

  function handleFollowVisitor(item: Item<FollowUser>) {
    setFilters(currentFilters => {
      return {...currentFilters, followVisitor: item.id};
    })
  }

  function handleDistanceThreshold(item: Item<DistanceThreshold>) {
    setFilters(currentFilters => {
      return {...currentFilters, distanceThreshold: item.id};
    })
  }

  function handleZoomLevel(item: Item<ZoomLevel>) {
    setFilters(currentFilters => {
      return {...currentFilters, zoomLevel: item.id};
    })
  }

  return (
    <Page title='Visitor Movement' breadcrumbs={breadcrumbs}>
      <div className='flex flex-col gap-4'>
        <Card>
          <div className='flex flex-wrap gap-4'>
            {/* Date */}
            <DateInput
              id="date"
              name="date"
              label="Date"
              value={filters.since}
              onChange={selectDate}
            />

            <div className="max-w-[10ch]">
              <Selector
                label='Follow visitor'
                items={followVisitorItems}
                selectedId={filters.followVisitor}
                onChange={handleFollowVisitor}
              />
            </div>

            <div className="max-w-[20ch]">
              <Selector
                label='Distance threshold'
                items={distanceThresholdItems}
                selectedId={filters.distanceThreshold}
                onChange={handleDistanceThreshold}
              />
            </div>

            <div className="max-w-[20ch]">
              <Selector
                label='Zoom level'
                items={zoomLevelItems}
                selectedId={filters.zoomLevel}
                onChange={handleZoomLevel}
              />
            </div>

            {isMultiSite && <SiteSelect id="select-site" name="site" label={`${termFor('Site')}`} onChange={selectSite} showAnySiteOption={false} />}
          </div>
        </Card>

        <div>
            <VisitorsMovementMap
              height="40rem"
              since={filters.since}
              until={filters.until}
              siteId={filters.siteId}
              followVisitor={filters.followVisitor === FOLLOW_VISITOR.YES}
              distanceThreshold={filters.distanceThreshold}
              zoomLevel={filters.zoomLevel}
            />
        </div>
      </div>
    </Page>
  );
}